<script lang="ts">
import { nextTick, ref } from 'vue';
import { uniqueId } from 'lodash';

export const elToastStates = ['error', 'warning', 'success'] as const;
export type ElToastStates = (typeof elToastStates)[number];

export interface ToastOptions {
  title: string;
  state: ElToastStates;
  description?: string;
  allowDuplicates?: boolean;
}

interface Toast {
  showing: boolean;
  options: ToastOptions;
  timeoutId: ReturnType<typeof setTimeout> | undefined;
  id: string;
}

const toasts = ref<Toast[]>([]);
const toastIdsToRemoveImmediately = ref<string[]>([]);

const close = async (toast: Toast, immediate?: boolean) => {
  if (immediate) {
    toastIdsToRemoveImmediately.value.push(toast.id);
    await nextTick();
  }
  toast.showing = false;
  if (toast.timeoutId) clearTimeout(toast.timeoutId);
  const index = toasts.value.findIndex(currentToast => currentToast.id === toast.id);
  if (index > -1) {
    toasts.value.splice(index, 1);
  }
};

const setTimeoutForToast = (toast: Toast, setNew = true) => {
  if (toast.timeoutId) clearTimeout(toast.timeoutId);
  if (setNew) {
    toast.timeoutId = setTimeout(() => {
      close(toast);
    }, 5000);
  }
};

const show = (args: ToastOptions) => {
  const { title, allowDuplicates } = args;
  if (!allowDuplicates && toasts.value.some(toast => toast.options.title === title)) return;
  toasts.value.push({
    showing: true,
    options: args,
    timeoutId: undefined,
    id: uniqueId(),
  });
  setTimeoutForToast(toasts.value[toasts.value.length - 1]);
};

export const toast = (msg: string, options: Partial<ToastOptions> = {}) => {
  options.title = msg;
  show({
    title: '',
    description: undefined,
    state: 'success',
    allowDuplicates: false,
    ...options,
  });
};
</script>

<script setup lang="ts">
import ElIcon from '@/ElIcon.vue';
</script>

<template>
  <Teleport to="body">
    <div class="pointer-events-none fixed inset-0 z-toast flex items-end px-4 py-6 sm:items-start sm:p-6">
      <TransitionGroup
        tag="ul"
        class="flex w-full flex-col items-center gap-4 sm:flex-col-reverse sm:items-end"
        enter-active-class="ease-out"
        enter-from-class="translate-y-4 sm:translate-y-0 sm:translate-x-[100%]"
        enter-to-class="translate-y-0 sm:translate-x-0"
        leave-active-class="ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
        :duration="400"
      >
        <li
          v-for="_toast of toasts"
          :key="_toast.id"
          :show="_toast.showing"
          :data-cy="`${_toast.options.state === 'success' ? 'confirm' : _toast.options.state}-toast`"
          :class="{
            'bg-green-50 border-green-300': _toast.options.state === 'success',
            'bg-red-50 border-red-300': _toast.options.state === 'error',
            'bg-yellow-50 border-yellow-300': _toast.options.state === 'warning',
            'transition duration-500': !toastIdsToRemoveImmediately.includes(_toast.id),
            'transition duration-0': toastIdsToRemoveImmediately.includes(_toast.id),
          }"
          class="pointer-events-auto flex items-center w-full max-w-sm transform overflow-hidden rounded-lg p-4 shadow border gap-3"
          @mouseover="setTimeoutForToast(_toast, false)"
          @mouseleave="setTimeoutForToast(_toast, true)"
        >
          <ElIcon v-if="_toast.options.state === 'success'" disabled class="h-7 w-7 text-green-500 shrink-0" name="CheckBadgeIcon" solid />
          <ElIcon
            v-if="_toast.options.state === 'warning'"
            disabled
            class="h-7 w-7 text-yellow-500 shrink-0"
            name="ExclamationTriangleIcon"
            solid
          />
          <ElIcon
            v-if="_toast.options.state === 'error'"
            disabled
            class="h-7 w-7 text-red-500 shrink-0"
            name="ExclamationCircleIcon"
            solid
          />

          <div
            class="flex flex-col grow justify-center text-sm gap-1"
            :class="{
              'text-green-700': _toast.options.state === 'success',
              'text-yellow-700': _toast.options.state === 'warning',
              'text-red-700': _toast.options.state === 'error',
            }"
          >
            <p class="font-semibold">
              {{ _toast.options.title }}
            </p>
            <p v-if="_toast.options.description">
              {{ _toast.options.description }}
            </p>
          </div>
          <ElIcon
            name="XMarkIcon"
            class="h-6 w-6 shrink-0 hover:cursor-pointer"
            :class="{
              'text-green-700': _toast.options.state === 'success',
              'text-yellow-700': _toast.options.state === 'warning',
              'text-red-700': _toast.options.state === 'error',
            }"
            @click="close(_toast, true)"
          />
        </li>
      </TransitionGroup>
    </div>
  </Teleport>
</template>
